import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/layout/Layout';
import Navbar from '../../components/layout/Navbar';
import DataTable from '../../components/common/DataTable';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import api from '../../api/axios';
import { ShieldCheck, Eye, CheckCircle, XCircle, Search, Clock } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const KYCManagement = () => {
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, verified: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [modalType, setModalType] = useState(null); // 'view' | 'reject'
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchKYC = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 10 };
      if (filter !== 'all') params.status = filter;
      if (search) params.search = search;
      
      const res = await api.get('/api/superadmin/kyc', { params });
      setMembers(res.data?.data?.members || []);
      setStats(res.data?.data?.summary || { total: 0, pending: 0, verified: 0, rejected: 0 });
      setTotalPages(res.data?.data?.pagination?.totalPages || 1);
    } catch (err) {
      toast.error('Failed to load KYC list');
    } finally {
      setLoading(false);
    }
  }, [filter, page, search]);

  useEffect(() => { fetchKYC(); }, [fetchKYC]);

  // Reset page when filter or search changes
  useEffect(() => { setPage(1); }, [filter, search]);

  const handleVerify = async (userId) => {
    try {
      setActionLoading(true);
      await api.put(`/api/superadmin/kyc/${userId}/verify`, { action: 'verify' });
      toast.success('KYC Verified successfully!');
      setModalType(null);
      fetchKYC();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to verify KYC');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return toast.error('Please provide a reason');
    try {
      setActionLoading(true);
      await api.put(`/api/superadmin/kyc/${selected.user_id}/verify`, { action: 'reject', reason: rejectReason });
      toast.success('KYC Rejected');
      setModalType(null);
      setRejectReason('');
      fetchKYC();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reject KYC');
    } finally {
      setActionLoading(false);
    }
  };



  const getStatusBadge = (status) => {
    const map = {
      not_submitted: { color: 'bg-gray-100 text-gray-700', label: 'Not Submitted' },
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      verified: { color: 'bg-green-100 text-green-800', label: 'Verified' },
      rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' },
    };
    const s = map[status] || map.not_submitted;
    return <span className={`px-2 py-1 text-xs font-semibold rounded-full ${s.color}`}>{s.label}</span>;
  };

  const columns = [
    { header: 'Member', id: 'name', cell: ({ row: { original: m } }) => (
      <div className="flex items-center gap-3">
        {m.profile_photo_url ? (
          <img src={m.profile_photo_url} alt="Profile" className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700">
            {m.name?.charAt(0)}
          </div>
        )}
        <div>
          <p className="font-semibold text-gray-900">{m.name}</p>
          <p className="text-xs text-gray-500">{m.phone}</p>
        </div>
      </div>
    )},
    { header: 'Location', id: 'location', cell: ({ row: { original: m } }) => (
      <span className="text-sm">{m.city ? `${m.city}, ${m.state}` : '—'}</span>
    )},
    { header: 'Submitted', id: 'date', cell: ({ row: { original: m } }) => (
      <span className="text-sm text-gray-600">
        {m.kyc_submitted_at ? format(new Date(m.kyc_submitted_at), 'dd MMM yyyy, p') : '—'}
      </span>
    )},
    { header: 'Status', id: 'status', cell: ({ row: { original: m } }) => getStatusBadge(m.kyc_status) },
    { header: 'Actions', id: 'actions', cell: ({ row: { original: m } }) => (
      <Button size="sm" variant="ghost" icon={Eye} onClick={() => { setSelected(m); setModalType('view'); }}>
        View Details
      </Button>
    )},
  ];

  return (
    <Layout>
      <Navbar title="KYC Management" subtitle="Verify and manage member KYC" onRefresh={fetchKYC} loading={loading} />
      <div className="p-6 space-y-6">
        
        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-4">
            <div className="p-3 bg-blue-50 text-blue-600 rounded-lg"><ShieldCheck size={24} /></div>
            <div><p className="text-xs text-gray-500 font-semibold uppercase">Total</p><p className="text-2xl font-bold">{stats.total}</p></div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-4">
            <div className="p-3 bg-yellow-50 text-yellow-600 rounded-lg"><Clock size={24} /></div>
            <div><p className="text-xs text-gray-500 font-semibold uppercase">Pending</p><p className="text-2xl font-bold">{stats.pending}</p></div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-4">
            <div className="p-3 bg-green-50 text-green-600 rounded-lg"><CheckCircle size={24} /></div>
            <div><p className="text-xs text-gray-500 font-semibold uppercase">Verified</p><p className="text-2xl font-bold">{stats.verified}</p></div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-4">
            <div className="p-3 bg-red-50 text-red-600 rounded-lg"><XCircle size={24} /></div>
            <div><p className="text-xs text-gray-500 font-semibold uppercase">Rejected</p><p className="text-2xl font-bold">{stats.rejected}</p></div>
          </div>
        </div>

        {/* Filters & Table */}
        <div className="card">
          <div className="flex gap-2 mb-6 border-b border-gray-100 pb-4 overflow-x-auto">
            {['all', 'pending', 'verified', 'rejected'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 text-sm font-medium rounded-lg capitalize transition-colors whitespace-nowrap ${filter === f ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:bg-gray-50'}`}
              >
                {f}
              </button>
            ))}
          </div>
          <DataTable 
            columns={columns} 
            data={members} 
            loading={loading} 
            emptyMessage="No KYC records found"
            serverSide={true}
            serverPage={page}
            serverTotalPages={totalPages}
            onPageChange={setPage}
            onSearchChange={setSearch}
          />
        </div>
      </div>

      {/* KYC Details Modal */}
      <Modal open={modalType === 'view' && !!selected} onClose={() => setModalType(null)} title="KYC Verification Details" size="xl">
        {selected && (
          <div className="flex flex-col lg:flex-row gap-6 max-h-[80vh] overflow-y-auto">
            {/* Left Col - Details */}
            <div className="flex-1 space-y-6">
              <div className="flex items-center gap-4 bg-gray-50 p-4 rounded-xl">
                {selected.profile_photo_url ? (
                  <img src={selected.profile_photo_url} alt="Profile" className="w-16 h-16 rounded-full object-cover border border-gray-200" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700 text-2xl">
                    {selected.name?.charAt(0)}
                  </div>
                )}
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{selected.name}</h3>
                  <p className="text-gray-500">{selected.email} • {selected.phone}</p>
                  <div className="mt-2">{getStatusBadge(selected.kyc_status)}</div>
                </div>
              </div>
              
              <div>
                <h4 className="font-bold text-gray-900 mb-2 border-b pb-1">Personal Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-500 block">Date of Birth</span> <span className="font-medium">{selected.date_of_birth || '—'}</span></div>
                  <div><span className="text-gray-500 block">Gender</span> <span className="font-medium">{selected.gender || '—'}</span></div>
                  <div className="col-span-2"><span className="text-gray-500 block">Address</span> <span className="font-medium">{[selected.address_line1, selected.address_line2, selected.city, selected.state, selected.pincode].filter(Boolean).join(', ') || '—'}</span></div>
                </div>
              </div>

              <div>
                <h4 className="font-bold text-gray-900 mb-2 border-b pb-1">Emergency Contact</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-500 block">Name</span> <span className="font-medium">{selected.emergency_contact_name || '—'}</span></div>
                  <div><span className="text-gray-500 block">Phone</span> <span className="font-medium">{selected.emergency_contact_phone || '—'}</span></div>
                  <div><span className="text-gray-500 block">Relation</span> <span className="font-medium">{selected.emergency_contact_relation || '—'}</span></div>
                </div>
              </div>

              <div>
                <h4 className="font-bold text-gray-900 mb-2 border-b pb-1">Bank Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-500 block">Bank Name</span> <span className="font-medium">{selected.bank_name || '—'}</span></div>
                  <div><span className="text-gray-500 block">Account Holder</span> <span className="font-medium">{selected.account_holder_name || '—'}</span></div>
                  <div><span className="text-gray-500 block">Account No.</span> <span className="font-medium font-mono">{selected.account_number || '—'}</span></div>
                  <div><span className="text-gray-500 block">IFSC</span> <span className="font-medium font-mono">{selected.ifsc_code || '—'}</span></div>
                  <div className="col-span-2"><span className="text-gray-500 block">UPI ID</span> <span className="font-medium">{selected.upi_id || '—'}</span></div>
                </div>
              </div>
            </div>

            {/* Right Col - Documents */}
            <div className="flex-1 space-y-6 bg-gray-50 p-6 rounded-xl border border-gray-100">
              <h4 className="font-bold text-gray-900 border-b pb-2">Identity Documents</h4>
              
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-gray-700">Aadhar Card</span>
                  <span className="text-sm font-mono bg-white px-2 py-1 rounded border shadow-sm">{selected.aadhar_number || '—'}</span>
                </div>
                {selected.aadhar_photo_url ? (
                  <a href={selected.aadhar_photo_url} target="_blank" rel="noreferrer" className="block w-full h-48 bg-gray-200 rounded-lg overflow-hidden border border-gray-300 hover:opacity-90 transition-opacity">
                    <img src={selected.aadhar_photo_url} alt="Aadhar" className="w-full h-full object-cover" />
                  </a>
                ) : (
                  <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 border border-dashed border-gray-300">No Aadhar Uploaded</div>
                )}
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-gray-700">PAN Card</span>
                  <span className="text-sm font-mono bg-white px-2 py-1 rounded border shadow-sm">{selected.pan_number || '—'}</span>
                </div>
                {selected.pan_photo_url ? (
                  <a href={selected.pan_photo_url} target="_blank" rel="noreferrer" className="block w-full h-48 bg-gray-200 rounded-lg overflow-hidden border border-gray-300 hover:opacity-90 transition-opacity">
                    <img src={selected.pan_photo_url} alt="PAN" className="w-full h-full object-cover" />
                  </a>
                ) : (
                  <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 border border-dashed border-gray-300">No PAN Uploaded</div>
                )}
              </div>

              {/* Action Buttons */}
              {selected.kyc_status === 'pending' && (
                <div className="pt-6 border-t border-gray-200 flex gap-3">
                  <Button variant="danger" className="flex-1" icon={XCircle} onClick={() => setModalType('reject')}>Reject</Button>
                  <Button variant="success" className="flex-1" icon={CheckCircle} onClick={() => handleVerify(selected.user_id)} loading={actionLoading}>Verify KYC</Button>
                </div>
              )}
              {selected.kyc_status === 'verified' && (
                <div className="p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg text-center font-medium">
                  This user's KYC has been verified.
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Reject Reason Modal */}
      <Modal open={modalType === 'reject'} onClose={() => setModalType('view')} title="Reject KYC" size="sm">
        <div className="space-y-4">
          <p className="text-gray-600 text-sm">Please provide a reason for rejecting the KYC documents. The member will see this message and be asked to resubmit.</p>
          <textarea
            className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-red-500 outline-none"
            rows="4"
            placeholder="e.g. Aadhar card photo is too blurry, or PAN number does not match image..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          ></textarea>
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" className="flex-1" onClick={() => setModalType('view')}>Cancel</Button>
            <Button variant="danger" className="flex-1" onClick={handleReject} loading={actionLoading}>Submit Rejection</Button>
          </div>
        </div>
      </Modal>

    </Layout>
  );
};

export default KYCManagement;
